import { useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { Truck } from 'lucide-react';
import { shippingApi } from '../../api/endpoints/shipping';
import { Modal } from '../../components/ui/Modal';
import { Button } from '../../components/ui/Button';
import { Input } from '../../components/ui/Input';
import { CurrencyDisplay } from '../../components/ui/CurrencyDisplay';
import { LoadingSpinner } from '../../components/ui/LoadingSpinner';
import { useToast } from '../../components/ui/Alert';
import { useStore } from '../../contexts/StoreContext';

interface Carrier {
  id: number;
  title: string;
  title_arabic?: string;
  carrier_logo?: string;
  rate?: number;
  approximate_delivery_time_arabic?: string;
  active?: boolean;
  warning?: string;
}

interface CreateShippingLabelModalProps {
  isOpen: boolean;
  onClose: () => void;
  orderId: string | null;
  storeId: string;
  paymentStatus?: string;
}

export function CreateShippingLabelModal({ isOpen, onClose, orderId, storeId, paymentStatus }: CreateShippingLabelModalProps) {
  const { t } = useTranslation();
  const { toast } = useToast();
  const queryClient = useQueryClient();
  const { currentStore } = useStore();
  const currency = currentStore?.currency || 'SAR';

  const [labelStep, setLabelStep] = useState<'weight' | 'carriers' | 'creating'>('weight');
  const [shipmentWeight, setShipmentWeight] = useState('1.0');
  const [boxCount, setBoxCount] = useState(1);
  const [availableCarriers, setAvailableCarriers] = useState<Carrier[]>([]);
  const [carriersLoading, setCarriersLoading] = useState(false);
  const [selectedCarrierId, setSelectedCarrierId] = useState<number | null>(null);
  const [creatingLabel, setCreatingLabel] = useState(false);

  const handleClose = () => {
    setLabelStep('weight');
    setShipmentWeight('1.0');
    setBoxCount(1);
    setAvailableCarriers([]);
    setSelectedCarrierId(null);
    onClose();
  };

  const fetchCarriers = async () => {
    if (!orderId) return;
    setCarriersLoading(true);
    try {
      const result = await shippingApi.listAvailableCarriers(storeId, {
        orderId,
        weight: parseFloat(shipmentWeight) || 1.0,
        boxCount,
      });
      setAvailableCarriers(result.companies || []);
      setLabelStep('carriers');
    } catch {
      toast('error', t('common.error'));
    } finally {
      setCarriersLoading(false);
    }
  };

  const handleCreateLabel = async () => {
    if (!selectedCarrierId || !orderId) return;
    setCreatingLabel(true);
    setLabelStep('creating');
    try {
      const result = await shippingApi.createLabel(storeId, {
        orderId,
        carrierId: String(selectedCarrierId),
        weight: parseFloat(shipmentWeight) || 1.0,
        boxCount,
      });
      queryClient.invalidateQueries({ queryKey: ['order', storeId, orderId] });
      queryClient.invalidateQueries({ queryKey: ['orders', storeId] });
      toast('success', t('orders.labelCreated', 'Shipping label created'));
      handleClose();
      if (result.shippingLabel) {
        window.open(result.shippingLabel, '_blank');
      }
    } catch {
      toast('error', t('common.error'));
      setLabelStep('carriers');
    } finally {
      setCreatingLabel(false);
    }
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={handleClose}
      title={t('orders.createLabel')}
      size="lg"
      footer={
        labelStep === 'weight' ? (
          <>
            <Button variant="secondary" onClick={handleClose}>{t('common.cancel')}</Button>
            <Button onClick={fetchCarriers} isLoading={carriersLoading}>
              {t('common.next', 'Next')}
            </Button>
          </>
        ) : labelStep === 'carriers' ? (
          <>
            <Button variant="secondary" onClick={() => setLabelStep('weight')}>{t('common.back', 'Back')}</Button>
            <Button onClick={handleCreateLabel} isLoading={creatingLabel} disabled={!selectedCarrierId}>
              <Truck className="h-4 w-4" />
              {t('orders.createLabel')}
            </Button>
          </>
        ) : null
      }
    >
      {labelStep === 'weight' && (
        <div className="space-y-4">
          {paymentStatus === 'pending' && (
            <div className="rounded-lg bg-warning/10 border border-warning/20 p-3 text-sm text-warning">
              {t('orders.codWarning', 'COD: Settlement may take 7-14 business days')}
            </div>
          )}
          <Input
            label={t('orders.shipmentWeight', 'Shipment Weight (kg)')}
            type="number"
            step="0.1"
            min="0.1"
            value={shipmentWeight}
            onChange={(e) => setShipmentWeight(e.target.value)}
            placeholder="1.0"
          />
          <Input
            label={t('orders.boxCount', 'Number of Boxes')}
            type="number"
            min="1"
            value={boxCount}
            onChange={(e) => setBoxCount(Math.max(1, Number(e.target.value) || 1))}
          />
        </div>
      )}

      {labelStep === 'carriers' && (
        <div className="space-y-3">
          {availableCarriers.length === 0 ? (
            <p className="text-sm text-text-muted text-center py-6">{t('orders.noCarriers', 'No shipping companies available')}</p>
          ) : (
            availableCarriers.map((carrier) => (
              <button
                key={carrier.id}
                type="button"
                onClick={() => setSelectedCarrierId(carrier.id)}
                className={`flex w-full items-center gap-4 rounded-lg border p-4 text-start transition-colors ${
                  selectedCarrierId === carrier.id
                    ? 'border-accent bg-accent/5'
                    : 'border-border-default hover:border-accent/50 hover:bg-bg-hover'
                }`}
              >
                {carrier.carrier_logo ? (
                  <img src={carrier.carrier_logo} alt="" className="h-10 w-10 rounded-lg object-contain" />
                ) : (
                  <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-bg-tertiary">
                    <Truck className="h-5 w-5 text-text-muted" />
                  </div>
                )}
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-medium">{carrier.title_arabic || carrier.title}</p>
                  {carrier.approximate_delivery_time_arabic && (
                    <p className="text-xs text-text-muted">{carrier.approximate_delivery_time_arabic}</p>
                  )}
                  {carrier.warning && (
                    <p className="text-xs text-warning mt-0.5">{carrier.warning}</p>
                  )}
                </div>
                {carrier.rate != null && (
                  <div className="text-end shrink-0">
                    <CurrencyDisplay amount={carrier.rate} currency={currency} className="font-semibold" />
                  </div>
                )}
              </button>
            ))
          )}
        </div>
      )}

      {labelStep === 'creating' && (
        <div className="flex items-center justify-center py-8">
          <LoadingSpinner />
        </div>
      )}
    </Modal>
  );
}
